import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { FileTreeItemType, useOpenDirectories } from '@dbt-labs/sourdough';

import { useAllAppliedViewBy } from '@/hooks/discovery/allAppliedViewBy';
import { ResponseAppliedLineageNode } from '@/hooks/discovery/appliedLineage';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { unique } from '@/util/array';
import {
  buildRelativePath,
  generateFile,
  generateFolder,
  getFolderHierarchy,
  VIRTUAL_FOLDER_PREFIX,
} from '@/util/file';
import { useDbtCloudProject } from './DbtCloudProjectProvider';

type NodeWithFilePath = ResponseAppliedLineageNode & { filePath: string };

export const ROOT_NODE_ID = 'root';

const hasFilePath = (
  node: Partial<ResponseAppliedLineageNode>,
): node is NodeWithFilePath => {
  return !!node.filePath;
};

type FileTreeData = {
  items: FileTreeItemType[] | undefined;
  visibleItems: FileTreeItemType[] | undefined;
  fileToUniqueIdMap: Record<string, string[] | undefined>;
  uniqueIdToFileMap: Record<string, string | undefined>;
  activeFilePath: string | undefined;
  clearActiveFile: () => void;
  selectFile: (filePath: string) => void;
  selectUniqueId: (uniqueId: string) => void;
  openDirectories: string[];
  setOpenDirectories: React.Dispatch<React.SetStateAction<string[] | undefined>>;
};

const FileTreeContext = createContext<FileTreeData>({
  items: undefined,
  visibleItems: undefined,
  activeFilePath: undefined,
  openDirectories: [],
  clearActiveFile: () => {},
  setOpenDirectories: () => {},
  selectFile: () => {},
  selectUniqueId: () => {},
  fileToUniqueIdMap: {},
  uniqueIdToFileMap: {},
});

export const useFileTree = () => {
  return useContext(FileTreeContext);
};

// a constant by Core/Mantle to indicate it's imported auto exposure (not merged with manual exposure)
export const IMPORTED_AUTO_EXPOSURE_PATH_PREFIX = 'imported from' as const;

export function pathIsImported(path: string) {
  return path
    .toLocaleLowerCase()
    .startsWith(IMPORTED_AUTO_EXPOSURE_PATH_PREFIX.toLocaleLowerCase());
}

export const FileTreeProvider: FC<PropsWithChildren<{}>> = ({ children }) => {
  const { projectName: dbtCloudProjectName, environmentId } = useDbtCloudProject();
  const { userId } = useEnvironmentScope();
  const [activeFilePath, updateFile] = useState<string>();

  const [fileTreeQuery] = useAllAppliedViewBy({
    environmentId,
    userId,
  });
  const rootNodeName = dbtCloudProjectName.replaceAll('/', '_');
  const { items, fileToUniqueIdMap, uniqueIdToFileMap } = useMemo(() => {
    const fileToUniqueIdMap: Record<string, string[] | undefined> = {};
    const uniqueIdToFileMap: Record<string, string | undefined> = {};
    if (fileTreeQuery.fetching || fileTreeQuery.error) {
      return { items: undefined, fileToUniqueIdMap, uniqueIdToFileMap };
    }
    const nodes = fileTreeQuery.data?.environment.applied?.lineage;
    const currentPackage = fileTreeQuery.data?.environment.dbtProjectName;
    const folderSet = new Set<string>();

    const resultItems: FileTreeItemType[] = [
      {
        id: rootNodeName,
        parent: ROOT_NODE_ID,
        text: dbtCloudProjectName,
        data: { pathType: 'directory' as const },
      },
    ];
    const existingPaths = new Set();
    nodes?.filter(hasFilePath).forEach((node) => {
      const uniqueId = parseUniqueId(node.uniqueId);
      let filePath = null;
      if (pathIsImported(node.filePath)) {
        // 'imported from tableau' -> '__virtual__imported_from_tableau'
        const virtualFolderName = `${
          VIRTUAL_FOLDER_PREFIX
        }${node.filePath.toLocaleLowerCase().replaceAll(' ', '_')}`;
        filePath = `${rootNodeName}/${virtualFolderName}/${node.name}`;
      } else {
        filePath =
          currentPackage === uniqueId.packageName
            ? node.filePath
            : buildRelativePath('dbt_packages', uniqueId.packageName, node.filePath);
        filePath = `${rootNodeName}/${filePath}`;
      }
      const file = generateFile(filePath, rootNodeName);
      filePath = file.id;
      fileToUniqueIdMap[filePath] ??= [];
      fileToUniqueIdMap[filePath]!.push(node.uniqueId);
      uniqueIdToFileMap[node.uniqueId] = filePath;
      getFolderHierarchy(filePath)?.forEach((folderPath) => {
        folderSet.add(folderPath);
      });
      folderSet.delete(filePath);
      folderSet.add(file.parent);
    });

    Object.entries(fileToUniqueIdMap).forEach(([filePath, uniqueIds]) => {
      if (!uniqueIds?.length) return;
      uniqueIds = uniqueIds.filter(unique);
      if (uniqueIds.length === 1) {
        const file = generateFile(filePath, rootNodeName);
        resultItems.push(file);
      } else {
        uniqueIds.forEach((uniqueId) => {
          const psuedoFilePath = buildRelativePath(
            filePath,
            parseUniqueId(uniqueId).remainder,
          );
          if (existingPaths.has(psuedoFilePath)) return;
          existingPaths.add(psuedoFilePath);
          const file = generateFile(psuedoFilePath, rootNodeName);
          fileToUniqueIdMap[file.id] ??= [];
          fileToUniqueIdMap[file.id]!.push(uniqueId);
          uniqueIdToFileMap[uniqueId] = file.id;
          folderSet.add(file.parent);
          resultItems.push(file);
        });
      }
    });

    if (!nodes?.length) return { items: [], fileToUniqueIdMap, uniqueIdToFileMap };
    folderSet.delete(rootNodeName);

    folderSet.forEach((relativePath) => {
      const folder = generateFolder(relativePath, rootNodeName);
      if (existingPaths.has(folder.id)) return;
      resultItems.push(folder);
    });

    return { items: resultItems, fileToUniqueIdMap, uniqueIdToFileMap };
  }, [
    fileTreeQuery.fetching,
    fileTreeQuery.error,
    fileTreeQuery.data?.environment.applied?.lineage,
    fileTreeQuery.data?.environment.dbtProjectName,
    rootNodeName,
    dbtCloudProjectName,
  ]);

  const { openDirectories, setOpenDirectories } = useOpenDirectories({
    activeFilePath,
    items: items || [],
    rootNodeName: ROOT_NODE_ID,
    dbtSubdirectory: ROOT_NODE_ID,
  });
  const openDirectoriesSet = useMemo(() => {
    return new Set(openDirectories);
  }, [openDirectories]);

  /** A subset of items, items in collapsed folders are not in here */
  const visibleItems = useMemo(() => {
    return openDirectoriesSet.size
      ? items?.filter(
          (item) =>
            openDirectoriesSet.has(item.parent) ||
            item.parent === ROOT_NODE_ID ||
            item.id === rootNodeName ||
            item.parent === rootNodeName,
        )
      : undefined;
  }, [items, openDirectoriesSet, rootNodeName]);

  const selectFile = useCallback(
    (filePath: string | undefined) => {
      if (filePath !== activeFilePath) {
        updateFile(filePath);

        const newOptionDirectories = [
          ...openDirectories,
          ...(getFolderHierarchy(filePath) || [rootNodeName]),
        ].filter(unique);

        setOpenDirectories(newOptionDirectories);
      }
    },
    [activeFilePath, openDirectories, rootNodeName, setOpenDirectories],
  );

  const selectUniqueId = useCallback(
    (uniqueId: string) => {
      selectFile(uniqueIdToFileMap[uniqueId]);
    },
    [selectFile, uniqueIdToFileMap],
  );

  // TODO: add initialOpenDirectories to useOpenDirectories in Sourdough
  useEffect(() => {
    setOpenDirectories([rootNodeName]);
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <FileTreeContext.Provider
      value={{
        items,
        visibleItems,
        fileToUniqueIdMap,
        uniqueIdToFileMap,
        setOpenDirectories,
        selectFile,
        selectUniqueId,
        clearActiveFile: useCallback(() => selectFile(undefined), [selectFile]),
        openDirectories,
        activeFilePath,
      }}
    >
      {children}
    </FileTreeContext.Provider>
  );
};
